import React, { FC, ChangeEventHandler, FocusEventHandler } from 'react';
import { useMedia } from '../util';
import { RADIO_BUTTON_VERTICAL_WIDTH } from '../constants';
import { time } from 'uniqid';
import { Error } from '../Error';
import { isEmpty } from 'lodash';

export type RadioButtonsProps = {
  id?: string;
  label?: string;
  direction?: 'col' | 'row';
  disabled?: boolean;
  isRequired?: boolean;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler;
  onFocus?: FocusEventHandler;
  className?: string;
  name: string;
  options: {
    label: React.ReactNode | string;
    value: string;
  }[];
  verticalWidth?: number;
  error?: string;
  showError?: boolean;
  showLabel?: boolean;
};

// allow children for fieldset legend
export type RadioButtonsWithChildren = RadioButtonsProps & { children?: React.ReactNode };

export const RadioButtons: FC<RadioButtonsWithChildren> = ({
  id = time('radio-'),
  direction = 'col',
  disabled = false,
  isRequired = false,
  value,
  onChange,
  onBlur = () => true,
  onFocus = () => true,
  className = '',
  options,
  verticalWidth = RADIO_BUTTON_VERTICAL_WIDTH,
  children,
  name,
  showLabel = true,
  error,
  showError = true
}) => {
  const isVertical = useMedia([`(max-width: ${verticalWidth}px)`], [true], false);
  const radioDirection = isVertical || direction === 'col' ? 'radio-col' : 'radio-row';
  return (
    <fieldset
      role="radiogroup"
      aria-required={isRequired}
      aria-invalid={!isEmpty(error)}
      id={id}
      data-testid={id}
      className={`radio-buttons ${className}`}>
      {children && (
        <legend id={`${id}-label`} className={`${!showLabel ? 'sr-only' : ''}`}>
          {children}
        </legend>
      )}
      <div className={`d-flex ${radioDirection}`}>
        {options?.map((radio, index) => (
          <div className="radio-button d-flex" key={`${index}-radio-${id}`}>
            <span>
              <input
                type="radio"
                id={`${id}-${radio.value}`}
                data-testid={`${id}-${radio.value}`}
                checked={value === radio.value}
                disabled={disabled}
                onChange={onChange}
                name={name}
                onBlur={onBlur}
                onFocus={onFocus}
                value={radio.value}
                tabIndex={0} // Ensure all radio buttons are focusable by Tab
              />
              <span aria-hidden="true" className={`${value === radio.value ? 'selected' : ''}`} />
            </span>
            <label
              htmlFor={`${id}-${radio.value}`}
              className={`radio-label my-auto ${disabled ? 'disabled' : ''}`}
              id={`${id}-${radio.value}-label`}>
              {radio.label}
            </label>
          </div>
        ))}
      </div>
      <Error errors={error} showError={showError} />
    </fieldset>
  );
};

export default RadioButtons;
